/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-controller-access-in-routes, ember/no-get, warp-drive/no-legacy-request-patterns */
import Route from '@ember/routing/route';
import { isEmpty } from '@ember/utils';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import moment from 'moment';
import { service } from '@ember/service';
import EmberObject from '@ember/object';

export default class PageRoute extends Route {
  queryParams = {
    from: {
      refreshModel: true,
    },
    to: {
      refreshModel: true,
    },
    groupIds: {
      refreshModel: true,
    },
  };

  // Services
  @service
  logging;

  @service
  session;

  @service
  store;

  model(params) {
    let startTime = params.from;
    let endTime = params.to;
    if (isEmpty(startTime)) {
      // No start time provided.  Default to 1 Jan of current year.
      startTime = moment().startOf('year').format('X');
    }
    if (isEmpty(endTime)) {
      // No end time provided.  Default to end of today.
      endTime = moment().endOf('day').format('X');
    }
    let year = moment(endTime, 'X').format('YYYY');

    let postFilters = {
      pageId: params.page_id,
      metaOnly: true,
      include: 'tags',
      startTime,
      endTime,
    };

    let parentModel = this.modelFor('data-sheets') ?? {};
    return hash({
      page: this.store.findRecord('posts/page', params.page_id),
      postMeta: this.store.query('posts/post', postFilters),
      tagCategories: this.store.findAll('posts/tag-category'),
      tags: this.store.query('posts/tag', {
        schoolId: this.get('session.activeSchool'),
      }),
      caregivers: parentModel.caregivers,
      staff: parentModel.staff,
      learners: parentModel.learners,
      terms: this.store.query('term', {
        schoolId: this.get('session.activeSchool'),
        year,
      }),
      groups: this.store.query('group', {
        year,
      }),
    });
  }

  afterModel(model) {
    super.afterModel(...arguments);

    // Build a map of all post authors data
    return this.getPostAuthors(model?.postMeta).then((authorsDict) => {
      return (model.postAuthors = authorsDict);
    });
  }

  getPostAuthors(posts) {
    let promises = (posts ?? []).map((post) => {
      let postId = post.get('id');
      // `author` is an async belongsTo: it resolves to null when the post
      // genuinely has no author, and rejects when the person can't be loaded
      // (API, authorization or transient error).
      return Promise.resolve(post.get('author'))
        .then((author) => {
          if (!author) {
            return null;
          }
          return {
            postId,
            authorId: author.get('id'),
            fullName: author.get('fullName'),
            type: author.get('type'),
          };
        })
        .catch((error) => {
          // Log it - a failed author is worth knowing about - but don't fail
          // the whole route over one post. The post drops out of the timeline;
          // every count on the page is derived from the posts themselves, so
          // nothing else is affected.
          this.logging.pushError(error, 'dataSheetsPage:getPostAuthors', {
            postId,
          });
          return null;
        });
    });

    return Promise.all(promises).then((results) => {
      let dictionary = EmberObject.create({});
      results.forEach((result) => {
        if (!result) {
          return;
        }
        let { postId, authorId, fullName, type } = result;
        dictionary[postId] = { authorId, fullName, type };
      });
      return dictionary;
    });
  }

  deactivate() {
    super.deactivate(...arguments);
    // Reset the filters
    this.controller.reset();
  }
}
